import * as React from 'react';

import { type VariantProps, cva } from 'class-variance-authority';
import {
  CheckIcon,
  ChevronDown,
  ChevronRight,
  WandSparkles,
  XCircle,
  XIcon,
} from 'lucide-react';

import { cn } from './lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  'vai-m-1 vai-transition vai-ease-in-out vai-delay-150 hover:vai--translate-y-1 hover:vai-scale-110 vai-duration-300',
  {
    variants: {
      variant: {
        default:
          'vai-border-foreground/10 vai-text-foreground vai-bg-card hover:vai-bg-card/80',
        secondary:
          'vai-border-foreground/10 vai-bg-secondary vai-text-secondary-foreground hover:vai-bg-secondary/80',
        destructive:
          'vai-border-transparent vai-bg-destructive vai-text-destructive-foreground hover:vai-bg-destructive/80',
        inverted: 'vai-inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;

    /**
     * Whether the option is disabled.
     * Optional, defaults to false.
     */
    disabled?: boolean;
  }[];

  /**
   * Whether to disable the clear all button.
   * Optional, defaults to false.
   */
  disableClearAll?: boolean;

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = 'Select options',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      disableClearAll = false,
      className,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'vai-flex vai-w-full vai-p-1 vai-rounded-md vai-border vai-min-h-10 vai-h-auto vai-items-center vai-justify-between vai-bg-inherit hover:vai-bg-inherit [&_svg]:vai-pointer-events-auto !vai-p-0',
              className,
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="vai-flex vai-justify-between vai-items-center vai-w-full">
                <div className="vai-flex vai-flex-wrap vai-items-center">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          isAnimating ? 'vai-animate-bounce' : '',
                          multiSelectVariants({ variant }),
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && (
                          <IconComponent className="vai-h-4 vai-w-4 vai-mr-2" />
                        )}
                        {option?.label}
                        {!option?.disabled && (
                          <XCircle
                            className="vai-ml-2 vai-h-4 vai-w-4 vai-cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        )}
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'vai-bg-transparent vai-text-foreground vai-border-foreground/1 hover:vai-bg-transparent',
                        isAnimating ? 'vai-animate-bounce' : '',
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      <span
                        className={`vai-text-blue-600 vai-text-sm vai-ml-2`}
                      >
                        {`+ ${selectedValues.length - maxCount} more`}
                      </span>
                      <XCircle
                        className="vai-ml-2 vai-h-4 vai-w-4 vai-cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="vai-flex vai-items-center vai-justify-between">
                  {!disableClearAll && (
                    <XIcon
                      className="vai-h-4 vai-mx-2 vai-cursor-pointer vai-text-muted-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                    />
                  )}
                  {isPopoverOpen ? (
                    <ChevronDown className="vai-h-4 vai-mx-2 vai-cursor-pointer vai-text-muted-foreground" />
                  ) : (
                    <ChevronRight className="vai-h-4 vai-mx-2 vai-cursor-pointer vai-text-muted-foreground" />
                  )}
                </div>
              </div>
            ) : (
              <div className="vai-flex vai-items-center vai-justify-between vai-w-full vai-mx-auto">
                <span className="vai-text-sm vai-text-muted-foreground vai-mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="vai-h-4 vai-cursor-pointer vai-text-muted-foreground vai-mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="vai-w-auto vai-p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  className="vai-cursor-pointer"
                >
                  <div
                    className={cn(
                      'vai-mr-2 vai-flex vai-h-4 vai-w-4 vai-items-center vai-justify-center vai-rounded-sm vai-border vai-border-primary',
                      selectedValues.length === options.length
                        ? 'vai-bg-primary vai-text-primary-foreground'
                        : 'vai-opacity-50 [&_svg]:vai-invisible',
                    )}
                  >
                    <CheckIcon className="vai-h-4 vai-w-4" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className="vai-cursor-pointer"
                      disabled={option.disabled}
                    >
                      <div
                        className={cn(
                          'vai-mr-2 vai-flex vai-h-4 vai-w-4 vai-items-center vai-justify-center vai-rounded-sm vai-border vai-border-primary',
                          isSelected
                            ? 'vai-bg-primary vai-text-primary-foreground'
                            : 'vai-opacity-50 [&_svg]:vai-invisible',
                        )}
                      >
                        <CheckIcon className="vai-h-4 vai-w-4" />
                      </div>
                      {option.icon && (
                        <option.icon className="vai-mr-2 vai-h-4 vai-w-4 vai-text-muted-foreground" />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="vai-flex vai-items-center vai-justify-between">
                  {selectedValues.length > 0 && !disableClearAll && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="vai-flex-1 vai-justify-center vai-cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator
                        orientation="vertical"
                        className="vai-flex vai-min-h-6 vai-h-full"
                      />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="vai-flex-1 vai-justify-center vai-cursor-pointer vai-max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'vai-cursor-pointer vai-my-2 vai-text-foreground vai-bg-background vai-w-3 vai-h-3',
              isAnimating ? '' : 'vai-text-muted-foreground',
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
